import { uuid } from "lib/uuid";

/**
 * Default caption styles matching common subtitle conventions
 */
export const DEFAULT_CAPTION_STYLE = {
  fontFamily: "Arial, sans-serif",
  fontSize: 48,
  fontWeight: "bold",
  fontStyle: "normal",
  color: "#FFFFFF",
  textAlign: "center",

  // Background box
  backgroundColor: "#000000",
  backgroundOpacity: 0.9,
  backgroundPadding: 16,
  borderRadius: 6,

  // Text effects
  stroke: null,
  shadow: {
    color: "#000000",
    blur: 4,
    x: 1,
    y: 1
  },

  // Animation
  animation: {
    entrance: "fadeIn",
    exit: "fadeOut",
    duration: 200 // ms
  }
};

/**
 * Default word style (used for per-word rich text styling)
 * When a word has no style property, it inherits from the segment's globalStyle
 */
export const DEFAULT_WORD_STYLE = {
  color: null, // null = inherit from segment
  fontWeight: null, // null = inherit, or "bold"
  fontStyle: null, // null = inherit, or "italic"
  textDecoration: null // null = inherit, or "underline"
};

/**
 * Default highlight settings for caption elements
 * These are element-level settings that apply to the currently active word
 */
export const DEFAULT_HIGHLIGHT_SETTINGS = {
  enabled: false, // Whether to highlight the currently playing word
  color: "#ffff00" // Highlight background color (yellow default)
};

/**
 * Default caption display settings
 */
export const DEFAULT_DISPLAY_SETTINGS = {
  maxCharsPerLine: 42,
  maxLinesPerSegment: 2,
  autoSplit: false,
  showOneAtATime: true,
  // Caption display mode: 'sentence' | 'word' | 'progressive'
  // - sentence: Shows full caption segments (default)
  // - word: Shows one word at a time
  // - progressive: Words appear progressively and stay visible until segment ends
  captionMode: "sentence"
};

/**
 * Gets the active caption segments for a given video time
 *
 * @param {Array} segments - Array of caption segments
 * @param {number} currentTime - Current video playback time in seconds
 * @param {boolean} showOneAtATime - Whether to show only one segment at a time
 * @returns {Array} Active segments at the given time
 */
export const getActiveSegments = (
  segments,
  currentTime,
  showOneAtATime = true
) => {
  const activeSegments = segments.filter(
    seg => currentTime >= seg.startTime && currentTime < seg.endTime
  );

  // If multiple segments are active but we only want one, return the first
  if (showOneAtATime && activeSegments.length > 1) {
    return [activeSegments[0]];
  }

  return activeSegments;
};

/**
 * Splits a caption segment into multiple segments at a given time
 *
 * @param {Object} segment - Segment to split
 * @param {number} splitTime - Time in seconds to split at
 * @returns {Array} Array of two segments
 */
export const splitSegmentAt = (segment, splitTime) => {
  if (splitTime <= segment.startTime || splitTime >= segment.endTime) {
    throw new Error("Split time must be within segment boundaries");
  }

  // Calculate approximate character position for split
  const duration = segment.endTime - segment.startTime;
  const splitRatio = (splitTime - segment.startTime) / duration;
  const splitIndex = Math.round(segment.text.length * splitRatio);

  // Try to split at a word boundary
  let finalSplitIndex = splitIndex;
  const spaceAfter = segment.text.indexOf(" ", splitIndex);
  const spaceBefore = segment.text.lastIndexOf(" ", splitIndex);

  if (
    spaceAfter !== -1 &&
    (spaceBefore === -1 || spaceAfter - splitIndex < splitIndex - spaceBefore)
  ) {
    finalSplitIndex = spaceAfter;
  } else if (spaceBefore !== -1) {
    finalSplitIndex = spaceBefore;
  }

  const firstText = segment.text.substring(0, finalSplitIndex).trim();
  const secondText = segment.text.substring(finalSplitIndex).trim();

  return [
    {
      ...segment,
      id: `seg_${uuid()}`,
      endTime: splitTime,
      text: firstText
    },
    {
      ...segment,
      id: `seg_${uuid()}`,
      startTime: splitTime,
      text: secondText
    }
  ];
};

/**
 * Merges two adjacent caption segments
 *
 * @param {Object} segment1 - First segment
 * @param {Object} segment2 - Second segment (must be adjacent)
 * @returns {Object} Merged segment
 */
export const mergeSegments = (segment1, segment2) => {
  if (segment1.endTime !== segment2.startTime) {
    throw new Error("Segments must be adjacent to merge");
  }

  return {
    ...segment1,
    id: `seg_${uuid()}`,
    endTime: segment2.endTime,
    text: `${segment1.text} ${segment2.text}`.trim(),
    words: [...(segment1.words || []), ...(segment2.words || [])]
  };
};

/**
 * Formats time in seconds to timestamp string (HH:MM:SS.mmm)
 *
 * @param {number} seconds - Time in seconds
 * @returns {string} Formatted timestamp
 */
export const formatTimestamp = seconds => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  const millis = Math.floor((seconds % 1) * 1000);

  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}:${secs
    .toString()
    .padStart(2, "0")}.${millis.toString().padStart(3, "0")}`;
};

/**
 * Exports captions to SRT format
 *
 * @param {Array} segments - Caption segments
 * @returns {string} SRT formatted string
 */
export const exportToSRT = segments => {
  return segments
    .map((seg, index) => {
      const startTime = formatTimestamp(seg.startTime).replace(".", ",");
      const endTime = formatTimestamp(seg.endTime).replace(".", ",");

      return `${index + 1}\n${startTime} --> ${endTime}\n${seg.text}\n`;
    })
    .join("\n");
};

/**
 * Exports captions to WebVTT format
 *
 * @param {Array} segments - Caption segments
 * @returns {string} WebVTT formatted string
 */
export const exportToWebVTT = segments => {
  const header = "WEBVTT\n\n";

  const cues = segments
    .map((seg, index) => {
      const startTime = formatTimestamp(seg.startTime);
      const endTime = formatTimestamp(seg.endTime);

      return `${index + 1}\n${startTime} --> ${endTime}\n${seg.text}\n`;
    })
    .join("\n");

  return header + cues;
};

/**
 * Validates a caption element structure
 *
 * @param {Object} captionElement - Caption element to validate
 * @returns {Object} { valid: boolean, errors: Array }
 */
export const validateCaptionElement = captionElement => {
  const errors = [];

  if (!captionElement.type || captionElement.type !== "caption") {
    errors.push("Invalid element type");
  }

  if (!captionElement.videoElementId) {
    errors.push("Missing videoElementId");
  }

  if (!captionElement.segments || !Array.isArray(captionElement.segments)) {
    errors.push("Missing or invalid segments array");
  }

  if (captionElement.segments) {
    captionElement.segments.forEach((seg, index) => {
      if (seg.startTime >= seg.endTime) {
        errors.push(`Segment ${index}: start time must be before end time`);
      }

      if (!seg.text || seg.text.trim() === "") {
        errors.push(`Segment ${index}: missing text`);
      }
    });
  }

  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Issue types for word timing validation
 */
export const WORD_TIMING_ISSUES = {
  ZERO_DURATION: "zero_duration",
  NEGATIVE_DURATION: "negative_duration",
  OVERLAP: "overlap",
  SAME_START_TIME: "same_start_time"
};

/**
 * Validates word timing data and returns any issues found
 *
 * @param {Array} words - Array of word objects with { word, start, end }
 * @returns {Object} { valid: boolean, issues: Array }
 */
export const validateWordTimings = words => {
  const issues = [];

  if (!Array.isArray(words) || words.length === 0) {
    return { valid: true, issues: [] };
  }

  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    const prevWord = words[i - 1];

    // Check for zero duration (start === end)
    if (word.start === word.end) {
      issues.push({
        type: WORD_TIMING_ISSUES.ZERO_DURATION,
        index: i,
        word: word.word,
        start: word.start,
        end: word.end,
        message: `Word "${word.word}" at index ${i} has zero duration (start === end === ${word.start})`
      });
    }

    // Check for negative duration (start > end)
    if (word.start > word.end) {
      issues.push({
        type: WORD_TIMING_ISSUES.NEGATIVE_DURATION,
        index: i,
        word: word.word,
        start: word.start,
        end: word.end,
        message: `Word "${word.word}" at index ${i} has negative duration (start: ${word.start} > end: ${word.end})`
      });
    }

    // Check for overlap with previous word (current start < previous end)
    if (prevWord && word.start < prevWord.end) {
      issues.push({
        type: WORD_TIMING_ISSUES.OVERLAP,
        index: i,
        word: word.word,
        prevWord: prevWord.word,
        prevEnd: prevWord.end,
        currentStart: word.start,
        message: `Word "${word.word}" at index ${i} overlaps with previous word "${prevWord.word}" (starts at ${word.start}, prev ends at ${prevWord.end})`
      });
    }

    // Check if this word starts at the exact same time as previous word
    // (different from overlap - this catches the case where prev has zero duration)
    if (prevWord && word.start === prevWord.start) {
      issues.push({
        type: WORD_TIMING_ISSUES.SAME_START_TIME,
        index: i,
        word: word.word,
        prevWord: prevWord.word,
        sharedStart: word.start,
        message: `Word "${word.word}" at index ${i} has same start time as previous word "${prevWord.word}" (both start at ${word.start})`
      });
    }
  }

  return {
    valid: issues.length === 0,
    issues
  };
};

/**
 * Default options for fixing word timings
 */
export const DEFAULT_FIX_OPTIONS = {
  minDuration: 0.05, // 50ms minimum duration for any word
  segmentStart: null, // Optional segment start time for boundary enforcement
  segmentEnd: null // Optional segment end time for boundary enforcement
};

/**
 * Checks if a word has timing issues
 * @param {Object} word - Word object with start/end
 * @param {Object} prevWord - Previous word (or null)
 * @param {number} segmentEnd - Segment end time (or null)
 * @param {number} minDuration - Minimum duration for a word
 * @returns {boolean} True if word has issues
 */
const hasTimingIssues = (word, prevWord, segmentEnd, minDuration) => {
  // Zero or negative duration
  if (word.end <= word.start) return true;

  // Overlaps with previous word
  if (prevWord && word.start < prevWord.end) return true;

  // Extends past segment end
  if (segmentEnd !== null && word.end > segmentEnd) return true;

  // Word is too short AND there's a gap before it (we can fix by moving start back)
  if (prevWord) {
    const duration = word.end - word.start;
    const gapBefore = word.start - prevWord.end;
    if (duration < minDuration && gapBefore > 0) return true;
  }

  return false;
};

/**
 * Fixes word timing issues in caption data
 * Simple approach: problematic words start at previous word's end and end at next word's start
 * This ensures continuous coverage with no gaps for smooth highlighting
 *
 * @param {Array} words - Array of word objects with { word, start, end }
 * @param {Object} options - Fix options { minDuration, segmentStart, segmentEnd }
 * @returns {Array} Fixed words array (new array, does not mutate original)
 */
export const fixWordTimings = (words, options = {}) => {
  if (!Array.isArray(words) || words.length === 0) {
    return [];
  }

  const { minDuration, segmentStart, segmentEnd } = {
    ...DEFAULT_FIX_OPTIONS,
    ...options
  };

  // Deep clone to avoid mutating original
  const fixedWords = words.map(w => ({ ...w }));

  // Fix each word with timing issues
  for (let i = 0; i < fixedWords.length; i++) {
    const word = fixedWords[i];
    const prevWord = fixedWords[i - 1];
    const nextWord = fixedWords[i + 1];

    if (hasTimingIssues(word, prevWord, segmentEnd, minDuration)) {
      // Start at previous word's end, or segment start, or keep current start
      if (prevWord) {
        word.start = prevWord.end;
      } else if (segmentStart !== null) {
        word.start = segmentStart;
      }

      // End at next word's start, or segment end, or use minimum duration
      if (nextWord) {
        word.end = nextWord.start;
      } else if (segmentEnd !== null) {
        word.end = segmentEnd;
      } else {
        word.end = word.start + minDuration;
      }
    }
  }

  // Ensure minimum duration for all words (but respect segment end)
  for (let i = 0; i < fixedWords.length; i++) {
    const word = fixedWords[i];
    const nextWord = fixedWords[i + 1];

    if (word.end - word.start < minDuration) {
      const desiredEnd = word.start + minDuration;

      // Don't extend past segment end or next word's start
      if (segmentEnd !== null && desiredEnd > segmentEnd) {
        word.end = segmentEnd;
      } else if (nextWord && desiredEnd > nextWord.start) {
        word.end = nextWord.start;
      } else {
        word.end = desiredEnd;
      }
    }
  }

  return fixedWords;
};

/**
 * Validates and optionally fixes word timings, returning both the result and any issues found
 *
 * @param {Array} words - Array of word objects
 * @param {Object} options - { autoFix: boolean, ...fixOptions }
 * @returns {Object} { words: Array, validation: { valid, issues }, wasFixed: boolean }
 */
export const validateAndFixWordTimings = (words, options = {}) => {
  const { autoFix = true, ...fixOptions } = options;

  const validation = validateWordTimings(words);

  if (validation.valid || !autoFix) {
    return {
      words,
      validation,
      wasFixed: false
    };
  }

  const fixedWords = fixWordTimings(words, fixOptions);
  const postFixValidation = validateWordTimings(fixedWords);

  return {
    words: fixedWords,
    validation: postFixValidation,
    originalIssues: validation.issues,
    wasFixed: true
  };
};

/**
 * Fixes word timings for all segments in a caption element
 *
 * @param {Object} captionElement - Caption element with segments
 * @param {Object} options - Fix options
 * @returns {Object} New caption element with fixed word timings
 */
export const fixCaptionElementWordTimings = (captionElement, options = {}) => {
  if (!captionElement.segments || !Array.isArray(captionElement.segments)) {
    return captionElement;
  }

  const fixedSegments = captionElement.segments.map(segment => {
    if (!segment.words || !Array.isArray(segment.words)) {
      return segment;
    }

    const { words: fixedWords } = validateAndFixWordTimings(
      segment.words,
      options
    );

    return {
      ...segment,
      words: fixedWords
    };
  });

  return {
    ...captionElement,
    segments: fixedSegments
  };
};
